/**
 * Theme-aware utility classes for consistent styling across sites
 * These classes use CSS custom properties that are dynamically set based on the site
 */

/**
 * Get theme-aware gradient classes
 * @param direction - The gradient direction (to-r, to-br, etc.)
 * @param from - The color type (primary, secondary, accent)
 * @param to - The color type (primary, secondary, accent)
 * @returns CSS classes for gradient
 */
export const getGradientClasses = (
  direction: 'to-r' | 'to-br' | 'to-bl' | 'to-b' | 'to-tr' | 'to-tl' | 'to-t' | 'to-l' = 'to-br',
  from: 'primary' | 'secondary' | 'accent' = 'primary',
  to: 'primary' | 'secondary' | 'accent' = 'secondary'
): string => {
  return `bg-gradient-${direction} from-[var(--gradient-from-${from})] to-[var(--gradient-to-${to})]`;
};

/**
 * Get sport-specific gradient background classes
 * @param type - The gradient type (primary, secondary, accent)
 * @returns CSS classes for sport-specific gradient background
 */
export const getSportGradientBg = (
  type: 'primary' | 'secondary' | 'accent' = 'primary'
): string => {
  return `bg-gradient-to-br from-[var(--color-${type}-500)]/20 to-[var(--color-${type}-600)]/20 border-[var(--border-${type})]`;
};

/**
 * Common theme-aware class combinations
 */
export const themeClasses = {
  // Text gradients
  gradientText: 'bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] bg-clip-text text-transparent',
  gradientTextReverse: 'bg-gradient-to-r from-[var(--color-secondary)] to-[var(--color-primary)] bg-clip-text text-transparent',

  // Neon effects
  neonText: 'neon-text', // Uses CSS class with custom properties
  neonGlow: 'shadow-[0_0_30px_var(--neon-color)]',

  // Buttons
  primaryButton: 'bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white',
  secondaryButton: 'bg-[var(--color-secondary)] hover:bg-[var(--color-secondary-dark)] text-white',
  accentButton: 'bg-[var(--color-accent)] hover:bg-[var(--color-accent-dark)] text-white',

  // Cards with theme-aware borders
  glasscardPrimary: 'glass-card border border-[var(--border-primary)]',
  glasscardSecondary: 'glass-card border border-[var(--border-secondary)]',
  glasscardAccent: 'glass-card border border-[var(--border-accent)]',

  // Badges and labels
  primaryBadge: 'bg-[var(--color-primary)]/10 text-[var(--color-primary)] border border-[var(--border-primary)]',
  secondaryBadge: 'bg-[var(--color-secondary)]/10 text-[var(--color-secondary)] border border-[var(--border-secondary)]',
  accentBadge: 'bg-[var(--color-accent)]/10 text-[var(--color-accent)] border border-[var(--border-accent)]',

  // Sport-specific backgrounds
  sportBgPrimary: 'bg-gradient-to-br from-[var(--color-primary-500)]/20 to-[var(--color-primary-600)]/20',
  sportBgSecondary: 'bg-gradient-to-br from-[var(--color-secondary-500)]/20 to-[var(--color-secondary-600)]/20',
  sportBgAccent: 'bg-gradient-to-br from-[var(--color-accent-500)]/20 to-[var(--color-accent-600)]/20',

  // Headers and titles
  pageTitle: 'text-4xl md:text-6xl font-black',
  sectionTitle: 'text-2xl md:text-4xl font-bold text-white',
  cardTitle: 'text-xl font-bold text-white',

  // Stats and metrics
  statValue: 'text-3xl font-bold text-white',
  statLabel: 'text-sm text-gray-400',

  // Icons with theme colors
  primaryIcon: 'text-[var(--color-primary)]',
  secondaryIcon: 'text-[var(--color-secondary)]',
  accentIcon: 'text-[var(--color-accent)]',
};

/**
 * Get sport-specific color for different sports
 * This maps sport types to appropriate gradient classes
 */
export const getSportColor = (sport: string): string => {
  const sportColorMap: Record<string, string> = {
    football: 'from-green-500/20 to-green-600/20 border-green-500/30',
    basketball: 'from-orange-500/20 to-orange-600/20 border-orange-500/30',
    baseball: 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
    hockey: 'from-purple-500/20 to-purple-600/20 border-purple-500/30',
  };

  // For theme-aware version, use CSS custom properties
  return 'bg-gradient-to-br ' + (sportColorMap[sport.toLowerCase()] || getSportGradientBg('primary'));
};

/**
 * Helper to conditionally apply theme classes
 */
export const conditionalThemeClass = (
  condition: boolean,
  trueClass: keyof typeof themeClasses,
  falseClass?: keyof typeof themeClasses
): string => {
  if (condition) {
    return themeClasses[trueClass];
  }
  return falseClass ? themeClasses[falseClass] : '';
};

/**
 * Combine multiple theme classes
 */
export const combineThemeClasses = (...classes: (keyof typeof themeClasses)[]): string => {
  return classes.map(cls => themeClasses[cls]).join(' ');
};